import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import rehypeAutolink from "rehype-autolink-headings";
import { Clock, ArrowLeft, Tag } from "lucide-react";
import { COOKBOOKS } from "../data/cookbooks";
import { COOKBOOK_BODIES } from "../content/cookbook-registry";
import { mdComponents } from "../components/article/mdComponents";
import TableOfContents from "../components/article/TableOfContents";
import ArticleSidebar from "../components/article/ArticleSidebar";
import Citations from "../components/article/Citations";

const DIFFICULTY_COLORS = {
  Beginner: "#15803D",
  Intermediate: "#BE185D",
  Advanced: "#7E22CE",
};

export default function CookbookDetail() {
  const { slug } = useParams();
  const book = COOKBOOKS.find((c) => c.slug === slug);
  if (!book) return <Navigate to="/cookbooks" replace />;
  const body = (COOKBOOK_BODIES[slug] || "").trim();
  const hasBody = body.length > 0 && body !== "# Placeholder";

  return (
    <div data-testid="cookbook-detail" className="bg-[#F5F5F0] min-h-screen pt-24 pb-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-8">
          <Link
            to="/cookbooks"
            className="font-arcade text-base text-[#15803D] hover:underline uppercase inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Back to cookbooks
          </Link>
        </div>

        <div
          className="grid gap-10
                     grid-cols-1
                     lg:grid-cols-[minmax(0,1fr)_240px]
                     xl:grid-cols-[220px_minmax(0,1fr)_240px]"
        >
          <aside className="hidden xl:block">
            <ArticleSidebar
              items={COOKBOOKS}
              currentSlug={slug}
              basePath="/cookbooks"
              label="COOKBOOKS"
            />
          </aside>

          <article className="min-w-0 max-w-[760px] mx-auto w-full">
            <header className="border-b-2 border-[#D4D4D4] pb-8 mb-10">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="font-arcade text-base uppercase border px-2 py-1 bg-white"
                  style={{
                    color: DIFFICULTY_COLORS[book.difficulty] || "#15803D",
                    borderColor: DIFFICULTY_COLORS[book.difficulty] || "#15803D",
                  }}
                >
                  {book.difficulty}
                </span>
                <p className="font-arcade text-base text-[#15803D] uppercase tracking-wide">
                  &gt; {book.category}
                </p>
              </div>
              <h1 className="font-pixel text-2xl md:text-4xl text-[#0A0A0A] leading-tight mb-5">
                {book.title}
              </h1>
              <p className="font-terminal text-sm text-[#0A0A0A]/65 leading-relaxed mb-5">
                {book.description}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-[#0A0A0A]/55">
                <span className="font-terminal text-xs flex items-center gap-1.5">
                  <Clock size={12} /> {book.read_time} min read
                </span>
              </div>
              {book.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-5">
                  {book.tags.map((t) => (
                    <span key={t} className="tag-chip text-sm flex items-center gap-1">
                      <Tag size={9} /> {t}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {hasBody ? (
              <>
                <div className="prose-melon" data-testid="article-prose">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[
                      rehypeSlug,
                      [rehypeAutolink, { behavior: "wrap" }],
                      rehypeKatex,
                    ]}
                    components={mdComponents}
                  >
                    {body}
                  </ReactMarkdown>
                </div>
                {book.references?.length > 0 && <Citations refs={book.references} />}
              </>
            ) : (
              <div className="border-2 border-[#D4D4D4] bg-white p-8" style={{ boxShadow: "4px 4px 0px 0px #15803D" }}>
                <p className="font-arcade text-2xl text-[#15803D] uppercase mb-3 text-center">
                  &gt; COMING SOON
                </p>
                <p className="font-terminal text-sm text-[#0A0A0A]/60 text-center mb-6">
                  This cookbook is being authored. In the meantime, here is the planned outline:
                </p>
                {book.steps?.length > 0 && (
                  <ol className="space-y-2">
                    {book.steps.map((s, i) => (
                      <li key={i} className="font-terminal text-sm text-[#0A0A0A]/80 flex gap-3">
                        <span className="text-[#15803D] font-bold">{String(i + 1).padStart(2, "0")}.</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-24">
              {hasBody && <TableOfContents containerSelector='[data-testid="article-prose"]' />}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
